import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { startFreshCasdoorSignin } from "@/lib/auth/casdoor";
import { useAuthV2 } from "@/lib/authv2/store";
import { listAccounts, pinLogin, type AccountListing } from "@/lib/authv2/api";
import { getFingerprint } from "@/lib/authv2/fingerprint";
import { markSessionSnapshotFresh } from "@/lib/authv2/refreshInterceptor";
import { STAFF_INACTIVE_CODE } from "@/lib/authv2/staffInactive";
import { goHomeAfterLogin } from "@/lib/authv2/navigation";
import { AccountChooser } from "@/components/AccountChooser";
import { PinPad } from "@/components/PinPad";
import { LoginBrandPanel } from "@/features/login/LoginBrandPanel";
import { BRAND_ART, BrandBand, BrandMark } from "@/components/BrandArt";

export const Route = createFileRoute("/login")({
  component: LoginPage,
});

function LoginPage() {
  const [accounts, setAccounts] = useState<AccountListing[] | null>(null);
  const [pickedSub, setPickedSub] = useState<string | null>(null);
  const [pinError, setPinError] = useState<string | null>(null);
  // needsResignin: PIN was correct but BE has no live session for this account
  // (refresh_token consumed in another tab, expired, or session revoked).
  // Recovery requires a full Casdoor signin — not another PIN attempt.
  const [needsResignin, setNeedsResignin] = useState(false);
  const [busy, setBusy] = useState(false);
  // QĐ 0412: app vừa buộc đăng xuất (nhân sự đã nghỉ) thì nói lý do ở đây.
  const [signedOutReason] = useState<string | null>(() => useAuthV2.getState().signedOutReason ?? null);

  // Intentionally NO auto-bounce on accessToken — Header "Đổi người"
  // navigates here while the user still has an active session, so we always
  // render the chooser. After a successful pin-login we navigate to
  // /home ourselves (onPin handler below).

  // Load chooser on mount. Stable deps (no closures over component state)
  // — fire once when the route renders.
  useEffect(() => {
    listAccounts(getFingerprint())
      .then(setAccounts)
      .catch(() => setAccounts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPick = (sub: string) => {
    setPickedSub(sub);
    setPinError(null);
    setNeedsResignin(false);
  };
  // Thêm tài khoản KHÔNG được gọi /auth/logout: cookie session_id lúc này vẫn
  // trỏ tới tài khoản đang đăng nhập, nên logout sẽ revoke session của tài
  // khoản đó. Sau khi enroll xong tài khoản mới, người dùng quay lại chọn tài
  // khoản cũ trong chooser sẽ nhận session_expired vì sessions.Latest lọc
  // revoked_at IS NULL — buộc phải đăng nhập Casdoor lại. Chỉ xoá state local
  // và xoá SSO của Casdoor; /auth/enroll sẽ tự ghi đè cookie session_id.
  const onAdd = async () => {
    const accessToken = useAuthV2.getState().accessToken;
    setPickedSub(null);
    setPinError(null);
    setNeedsResignin(false);
    setBusy(true);
    useAuthV2.getState().clear();
    await startFreshCasdoorSignin(accessToken);
  };

  const onPin = async (pin: string) => {
    if (!pickedSub) return;
    setBusy(true);
    setPinError(null);
    try {
      const out = await pinLogin({
        fingerprint: getFingerprint(),
        casdoor_sub: pickedSub,
        pin,
      });
      useAuthV2.getState().setSession({
        accessToken: out.access_token ?? null,
        casdoorSub: out.casdoor_sub,
        displayName: out.display_name,
        staff: out.staff,
        permissions: out.permissions ?? [],
        branchScope: out.branchScope,
        dataVisibility: out.dataVisibility,
        persona: out.persona,
      });
      markSessionSnapshotFresh();
      goHomeAfterLogin();
    } catch (e) {
      const err = e as { status?: number; code?: string; message?: string };
      const msg = err.message ?? "";
      // BE supplies a `code` field on auth errors (handler.go pinLogin).
      // Branch on it so the PIN was-correct-but-session-dead path tells
      // the user to re-signin instead of "Mã PIN không đúng".
      switch (err.code) {
        case "account_locked":
          setPinError(`Tài khoản tạm khoá. ${msg}`);
          break;
        case "session_expired":
          setPinError("Phiên đăng nhập đã hết hạn. Cần đăng nhập lại bằng Casdoor để tiếp tục.");
          setNeedsResignin(true);
          break;
        case "upstream_refresh_failed":
          setPinError(
            "Phiên ở thiết bị này đã được làm mới ở nơi khác. Vui lòng đăng nhập lại bằng Casdoor.",
          );
          setNeedsResignin(true);
          break;
        case "provider_temporarily_unavailable":
          setPinError(
            "Casdoor đang tạm thời gián đoạn. Phiên trên thiết bị vẫn được giữ; vui lòng thử lại sau.",
          );
          break;
        case "not_enrolled":
          setPinError("Tài khoản này chưa được đăng ký trên thiết bị. Hãy đăng nhập bằng Casdoor.");
          setNeedsResignin(true);
          break;
        case STAFF_INACTIVE_CODE:
          // QĐ 0412: đăng nhập lại bằng Casdoor cũng không mở được — chỉ nói
          // đúng câu máy chủ, không mời đăng nhập lại.
          setPinError(msg || "Tài khoản này thuộc nhân sự đã nghỉ việc — liên hệ phòng nhân sự");
          break;
        case "pin_invalid":
        default:
          setPinError("Mã PIN không đúng");
      }
      setBusy(false);
    }
  };

  const picked = pickedSub ? (accounts?.find((a) => a.casdoor_sub === pickedSub) ?? null) : null;

  return (
    <div className="flex h-full flex-col overflow-y-auto lg:flex-row">
      <LoginBrandPanel />
      {/* Stacked (below lg) the brand panel alone is taller than a phone, so a
          plain flex-1 (basis 0, shrinkable) squeezes this pane to 0px and its
          overflow-hidden clips the card — nothing left to scroll. Keep it at
          content height there; side-by-side (lg) goes back to flex-1. */}
      <div className="relative flex flex-[1_0_auto] items-center justify-center overflow-hidden p-4 py-12 lg:flex-1">
        {/* Mockup 28/09/2026: thin lattice strip on top, the G corner hairline
            hugging the bottom-left. Decoration only — the card is unchanged. */}
        <BrandBand
          src={BRAND_ART.latticeBand}
          height={22}
          className="absolute inset-x-0 top-0 text-[var(--gn-gold)] opacity-50"
        />
        <BrandMark
          src={BRAND_ART.cornerG}
          className="absolute bottom-0 left-0 h-[150px] w-[190px] text-[var(--gn-gold)] opacity-70 lg:h-[210px] lg:w-[260px]"
          style={{ WebkitMaskPosition: "left bottom", maskPosition: "left bottom" }}
        />
        <div className="relative w-full max-w-sm rounded-lg border bg-surface p-8 shadow-sm">
          <h2 className="mb-1 text-[30px] font-normal leading-tight" style={{ fontFamily: "var(--font-art)" }}>
            Đăng nhập
          </h2>
          <p className="mb-6 text-sm text-muted">
            {picked ? `Nhập PIN của ${picked.display_name}` : "Chọn tài khoản"}
          </p>
          {signedOutReason ? (
            <p role="alert" className="mb-4 text-sm text-danger-text">
              {signedOutReason}
            </p>
          ) : null}

          {accounts === null ? (
            <p className="text-sm text-muted">Đang tải…</p>
          ) : picked ? (
            <>
              <PinPad onSubmit={onPin} error={pinError} busy={busy} />
              {needsResignin && (
                <button
                  onClick={onAdd}
                  className="mt-4 w-full rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
                >
                  Đăng nhập lại bằng Casdoor
                </button>
              )}
              <button
                onClick={() => {
                  setPickedSub(null);
                  setNeedsResignin(false);
                }}
                className="mt-4 w-full text-sm text-muted hover:text-text"
              >
                ← Đổi tài khoản khác
              </button>
            </>
          ) : (
            <AccountChooser accounts={accounts} onPick={onPick} onAdd={onAdd} />
          )}
        </div>
      </div>
    </div>
  );
}
